import { Button, Input, PageHeader, Select, Textarea, useToast } from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { useReleaseProducts } from '@/features/release-config/hooks';
import { filtersFromParams } from '@/features/releases/ReleaseFilters';
import { useCreateDraft, useSuggestVersion } from '@/features/releases/hooks';
import { useNavigate, useSearchParams } from '@solidjs/router';
import {
  ApiClientError,
  RELEASE_ENVIRONMENTS,
  type ReleaseEnvironment,
  VERSION_PATTERN,
  hasAnyReleaseCap,
  scopedReleasePairs,
} from '@uchat-team-management/shared';
import { For, Show, createEffect, createMemo, createSignal } from 'solid-js';

/**
 * Staged draft flow (revision #4), step 1: product + environment + version
 * only. Creating reserves the build number; refs, workflows, and the compare
 * baseline are edited on the draft detail page afterwards.
 */
export default function ReleaseVersionNew() {
  const products = useReleaseProducts();
  const create = useCreateDraft();
  const toast = useToast();
  const navigate = useNavigate();
  const { session } = useAuth();

  const [params] = useSearchParams();
  const [productId, setProductId] = createSignal('');
  const [environment, setEnvironment] = createSignal<ReleaseEnvironment>(
    filtersFromParams(params).environment ?? 'dev',
  );

  // UCH-419: scoped create grants limit the product/environment choices.
  const perms = () => session()?.permissions ?? new Set<string>();
  const creatableProducts = () =>
    (products.data?.data ?? []).filter((p) => hasAnyReleaseCap(perms(), 'create', p.slug));
  const allowedEnvironments = createMemo<readonly ReleaseEnvironment[]>(() => {
    if (perms().has('releases:drafts:create')) return RELEASE_ENVIRONMENTS;
    const slug = (products.data?.data ?? []).find((p) => p.id === productId())?.slug;
    if (!slug) return RELEASE_ENVIRONMENTS;
    const envs = scopedReleasePairs(perms(), 'create')
      .filter((pair) => pair.slug === slug)
      .map((pair) => pair.env);
    return RELEASE_ENVIRONMENTS.filter((e) => envs.includes(e));
  });
  let prefilledProduct = false;
  createEffect(() => {
    if (prefilledProduct || !products.data) return;
    prefilledProduct = true;
    const selected = creatableProducts().find((p) => p.id === params.product);
    if (selected) {
      setProductId(selected.id);
      if (!allowedEnvironments().includes(environment())) {
        setEnvironment(allowedEnvironments()[0] ?? 'dev');
      }
    }
  });

  const [version, setVersion] = createSignal(String(params.version ?? ''));
  const [description, setDescription] = createSignal('');
  const [error, setError] = createSignal<string | null>(null);

  const suggestInput = createMemo(() =>
    productId() ? { productId: productId(), environment: environment() } : null,
  );
  const suggest = useSuggestVersion(suggestInput);

  const submit = async () => {
    setError(null);
    if (!productId()) {
      setError('Select a product');
      return;
    }
    if (!VERSION_PATTERN.test(version())) {
      setError('Version must be MAJOR.MINOR.PATCH (e.g. 1.4.2)');
      return;
    }
    try {
      const resp = await create.mutateAsync({
        productId: productId(),
        environment: environment(),
        version: version(),
        description: description().trim(),
      });
      toast.success(`Draft ${resp.data.version} #${resp.data.buildNumber} created`);
      navigate(`/release/versions/${resp.data.id}`);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.error.message);
      } else {
        setError('Failed to create draft');
      }
    }
  };

  return (
    <section>
      <PageHeader
        title="New release draft"
        sub="Creating the draft reserves the next build number. Refs, workflows, and the compare baseline come next on the draft page."
      />

      <div class="mx-auto max-w-6xl space-y-4 p-6">
        <div class="flex flex-col gap-3">
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Select
              label="Product"
              value={productId()}
              onChange={(v) => {
                setProductId(v);
                if (!allowedEnvironments().includes(environment())) {
                  setEnvironment(allowedEnvironments()[0] ?? 'dev');
                }
              }}
              placeholder="Select product"
              options={creatableProducts().map((p) => ({ value: p.id, label: p.name }))}
              required
            />
            <Select
              label="Environment"
              value={environment()}
              onChange={(v) => setEnvironment(v as ReleaseEnvironment)}
              options={allowedEnvironments().map((e) => ({ value: e, label: e.toUpperCase() }))}
              required
            />
          </div>

          <div>
            <Input
              label="Version"
              value={version()}
              onInput={(e) => setVersion(e.currentTarget.value)}
              placeholder="1.4.2"
              required
            />
            <Show when={suggest.data?.data}>
              {(s) => (
                <div class="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-neutral-500">
                  <Show when={s().publishedVersion}>
                    {(published) => <p class="w-full">Published: {published()}</p>}
                  </Show>
                  <span>{s().base ? `from ${s().source} ${s().base}:` : 'suggested:'}</span>
                  <For each={s().reusableVersions}>
                    {(version) => (
                      <Button size="sm" variant="ghost" onClick={() => setVersion(version)}>
                        rebuild {version}
                      </Button>
                    )}
                  </For>
                  <For each={[s().suggestions.major, s().suggestions.minor, s().suggestions.patch]}>
                    {(sv) => (
                      <Button size="sm" variant="ghost" onClick={() => setVersion(sv)}>
                        {sv}
                      </Button>
                    )}
                  </For>
                </div>
              )}
            </Show>
          </div>

          <Textarea
            label="Description"
            rows={2}
            maxLength={255}
            placeholder="What this release carries (shown and searchable in the versions list)"
            value={description()}
            onInput={(e) => setDescription(e.currentTarget.value)}
          />

          <Show when={error()}>
            <p class="text-sm text-red-600">{error()}</p>
          </Show>

          <div class="flex justify-end">
            <Button onClick={submit} loading={create.isPending}>
              Create draft
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
